import { createStyles } from 'antd-style';
import type { ThemeColors } from '../../components/uchat/UCIndependentChat/hooks/useTheme';

export const useStyle = createStyles(({ css }, colors: ThemeColors) => {
  return {
    layout: css`
      height: 100%;
      min-height: 0; /* 重要：允许 flex 子元素缩小 */
      display: flex;
      flex-direction: column;
    `,
    chat: css`
      flex: 1;
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0; /* 重要：允许 flex 子元素缩小 */
      position: relative;
      background: ${colors.background};
      transition: background-color 0.3s ease;
    `,
    messages: css`
      flex: 1;
      min-height: 0; /* 重要：允许 flex 子元素缩小，使 overflow 生效 */
      overflow: auto;
      padding: 24px;
      padding-bottom: 8px;
      scroll-padding-bottom: 160px;
      color: ${colors.text};
      
      /* 确保消息文字颜色正确 */
      .ant-typography {
        color: ${colors.text} !important;
      }
      
      /* Markdown 渲染的内容 */
      p, div, span, li, td, th {
        color: ${colors.text} !important;
      }
      
      /* 链接颜色 */
      a {
        color: ${colors.evaluationMeta} !important;
      }
      
      /* 代码块背景 */
      code {
        background: ${colors.hoverBg};
        color: ${colors.text};
      }
      
      pre {
        background: ${colors.hoverBg};
        color: ${colors.text};
      }
      
      /* 暗色模式下 TextArea 的 placeholder 颜色 */
      .dark-textarea::placeholder {
        color: ${colors.senderPlaceholder} !important;
      }
      .dark-textarea::-webkit-input-placeholder {
        color: ${colors.senderPlaceholder} !important;
      }
      .dark-textarea::-moz-placeholder {
        color: ${colors.senderPlaceholder} !important;
        opacity: 1;
      }
      .dark-textarea:-ms-input-placeholder {
        color: ${colors.senderPlaceholder} !important;
      }
      
      /* Ant Design TextArea 的 placeholder */
      .ant-input.dark-textarea::placeholder,
      textarea.dark-textarea::placeholder {
        color: ${colors.senderPlaceholder} !important;
      }
    `,
    senderSection: css`
      position: sticky;
      bottom: 0;
      left: 0;
      right: 0;
      background: ${colors.senderBg};
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding-top: 4px;
      padding-bottom: 4px;
      /* 顶部轻微阴影，强调分隔 */
      box-shadow: 0 -4px 12px ${colors.shadow};
      z-index: 1;
      transition: background-color 0.3s ease, box-shadow 0.3s ease;
    `,
    /** 顶部分类下方「客服正在输入」状态条：始终占位，不进入消息列表 */
    typingHint: css`
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      box-sizing: border-box;
      height: 32px;
      padding: 0 16px;
      color: ${colors.senderPlaceholder};
      font-size: 12px;
      line-height: 1.4;
      user-select: none;
      pointer-events: none;
      background: ${colors.background};
      border-bottom: 1px solid ${colors.senderBorder || "transparent"};
    `,
    /** 未输入时仅隐藏内容，高度与边框仍保留，避免布局跳动 */
    typingHintIdle: css`
      visibility: hidden;
    `,
    typingHintText: css`
      opacity: 0.85;
    `,
    typingDots: css`
      display: inline-flex;
      align-items: center;
      gap: 3px;
      i {
        display: inline-block;
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: currentColor;
        opacity: 0.35;
        animation: uchatTypingDot 1.2s infinite ease-in-out;
      }
      i:nth-child(2) {
        animation-delay: 0.2s;
      }
      i:nth-child(3) {
        animation-delay: 0.4s;
      }
      @keyframes uchatTypingDot {
        0%,
        80%,
        100% {
          opacity: 0.25;
          transform: translateY(0);
        }
        40% {
          opacity: 0.9;
          transform: translateY(-2px);
        }
      }
    `,
    sender: css`
      margin: 0 auto;
      padding: 8px 0 0 0 !important;
      padding-bottom: 0 !important;
      margin-bottom: 0 !important;
      width: 100%;
      background: ${colors.senderBg}; /* 与聊天页背景一致 */
      /* 无边框外观覆盖 Sender 默认边框与阴影 */
      box-shadow: none !important;
      border-color: transparent !important;
      transition: background-color 0.3s ease;
      &:after {
        border-width: 0 !important;
      }
      &:focus-within {
        box-shadow: none !important;
        border-color: transparent !important;
      }
      &:focus-within:after {
        border-width: 0 !important;
      }

      /* 去除 Sender.Header 的下边框与背景、内边距 */
      .ant-x-sender-header {
        border: 0 !important;
        border-bottom: 0 !important;
        border-bottom-color: transparent !important;
        box-shadow: none !important;
        background: ${colors.senderBg} !important;
        height: 0 !important;
        min-height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
        position: relative !important;
        overflow: visible !important;
      }
      .ant-x-sender-header-motion {
        border: 0 !important;
        border-bottom: 0 !important;
        border-bottom-color: transparent !important;
        box-shadow: none !important;
        background: ${colors.senderBg} !important;
      }
      /* 覆盖所有 motion 派生 class，确保动画阶段也无边框 */
      [class*="ant-x-sender-header-motion"] {
        border: 0 !important;
        border-bottom: 0 !important;
        border-bottom-color: transparent !important;
        box-shadow: none !important;
      }
      .ant-x-sender-header-header {
        background: ${colors.senderBg} !important;
        padding-block: 0 !important;
        padding-inline-start: 0 !important;
        padding-inline-end: 0 !important;
        display: block !important;
        border-radius: 0 !important;
        border: 0 !important;
        box-shadow: none !important;
        height: 0 !important;
      }
      /* 只显示关闭按钮，且绝对定位到右上 */
      .ant-x-sender-header-close {
        position: absolute !important;
        top: -28px !important;
        right: 8px !important;
      }
      .ant-x-sender-header-close .ant-btn {
        padding: 0 !important;
        height: 24px !important;
        width: 24px !important;
        line-height: 24px !important;
        border-radius: 50% !important;
        background: transparent !important;
      }
      .ant-x-sender-header-content { display: none !important; }
      .ant-x-sender-header-title {
        display: none !important; /* 不展示标题，只保留关闭按钮 */
      }
      .ant-x-sender-header-close .ant-btn {
        padding: 0 !important;
        height: 24px !important;
        width: 24px !important;
        line-height: 24px !important;
        border-radius: 50% !important;
        background: transparent !important;
      }
      .ant-x-sender-header-close .ant-btn:hover,
      .ant-x-sender-header-close .ant-btn:focus {
        background: transparent !important;
      }

      /* 兜底把 Sender 根及内容区的任何边框去掉，避免残留细线 */
      & {
        border: 0 !important;
        box-shadow: none !important;
        outline: none !important;
      }
      &::before,
      &::after {
        border: 0 !important;
        border-width: 0 !important;
        box-shadow: none !important;
        outline: none !important;
      }
      > .ant-x-sender-content {
        border: 0 !important;
        border-top: 0 !important;
        box-shadow: none !important;
        outline: none !important;
        padding-block: 12px 0px !important; /* 覆盖默认的 padding-block: 12px，移除底部间距 */
        padding-bottom: 0 !important; /* 确保底部没有间距 */
      }
      > div { /* 直接子元素兜底去边框 */
        border-bottom: 0 !important;
        border-top: 0 !important;
      }
      
      /* 输入框容器（兼容 Sender 内部结构） */
      .ant-input,
      .ant-input-affix-wrapper,
      .ant-input-textarea-affix-wrapper,
      .ant-input-textarea {
        background: ${colors.senderBg};
        border: 1px solid ${colors.senderBorder} !important;
        border-radius: 28px !important;
        font-size: 15px;
        padding: 12px 52px 12px 16px !important; /* 为右侧圆形发送按钮预留空间 */
        line-height: 22px;
        box-shadow: 0 1px 0 ${colors.shadow} inset; /* 轻微内阴影，贴近稿件质感 */
        color: ${colors.text};
        transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;
      }

      .ant-input::placeholder {
        color: ${colors.senderPlaceholder};
      }

      /* 发送动作区域靠右内嵌到输入框内侧 */
      .ant-x-sender-content {
        position: relative; /* 作为 actions 的定位上下文 */
        padding-block: 12px 0px !important; /* 覆盖默认的 padding-block: 12px，移除底部间距 */
        padding-bottom: 0 !important; /* 确保底部没有间距 */
        margin-bottom: 0 !important; /* 确保底部没有外边距 */
      }
      .ant-x-sender-actions {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        gap: 0;
      }

      /* 发送按钮 */
      .ant-btn-primary,
      .ant-btn.ant-btn-primary {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        padding: 0;
        background: #1677ff;
        box-shadow: none; /* 贴近示意图的纯色圆形按钮 */
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
    `,
    senderActions: css`
      position: absolute;
      right: 25px;
      top: 68%;
      transform: translateY(-50%);
      display: flex;
      align-items: center;
      gap: 0;
      pointer-events: auto;
    `,
    senderToolbar: css`
      width: 92%;
      margin: 8px auto 16px auto;
      padding: 0 24px;
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 24px;
      align-items: center;
      justify-items: center;
      transform: none;
    `,
    senderToolbarItem: css`
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 8px;
      border: none;
      background: transparent;
      color: ${colors.background === '#14172B' ? '#ffffff' : '#000000'};
      font-size: 18px;
      margin: 0 auto;
      transition: all .2s ease;
      &:hover {
        color: ${colors.background === '#14172B' ? '#ffffff' : '#000000'};
        background: ${colors.hoverBg};
      }
    `,
    imagePreviewRow: css`
      padding: 0 24px;
      // overflow: hidden;
      will-change: transform, opacity;
      transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1),
                  opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      transform-origin: top;
    `,
    imagePreview: css`
      position: relative;
      display: inline-flex;
      align-items: center;
      
      img {
        width: 40px;
        height: 40px;
        object-fit: cover;
        border-radius: 4px;
        border: 1px solid ${colors.borderSecondary};
        vertical-align: middle;
        will-change: transform;
        backface-visibility: hidden;
        transform: translateZ(0);
      }
    `,
    removeImage: css`
      position: absolute;
      right: -6px;
      top: -6px;
      width: 14px;
      height: 14px;
      line-height: 12px;
      text-align: center;
      border-radius: 50%;
      background: ${colors.removeButtonBg};
      color: #fff;
      cursor: pointer;
      font-size: 10px;
      will-change: transform;
      transform: translateZ(0);
      transition: background-color 0.2s ease;
      
      &:hover {
        background: ${colors.removeButtonBgHover};
      }
    `,
    imageLoading: css`
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: ${colors.imageLoadingBg};
      padding: 4px;
      border-radius: 4px;
    `,
    messageContainer: css`
      display: flex;
      flex-direction: column;
      gap: 8px;
    `,
    messageText: css`
      word-break: break-word;
      white-space: pre-wrap;
    `,
    messageImage: css`
      margin-top: 8px;
      
      img {
        max-width: 200px;
        max-height: 200px;
        border-radius: 4px;
        object-fit: contain;
        will-change: transform;
        backface-visibility: hidden;
        transform: translateZ(0);
        cursor: pointer;
        
        &:hover {
          opacity: 0.9;
        }
      }
    `,
    imagePreviewModal: css`
      img {
        max-width: 100%;
        max-height: 80vh;
        object-fit: contain;
        will-change: transform;
        backface-visibility: hidden;
        transform: translateZ(0);
      }
    `,
    evaluationCompletedCard: css`
      padding: 24px;
      border-radius: 16px;
      border: 1px solid ${colors.evaluationBorder};
      background: ${colors.evaluationBg};
      transition: background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
      box-shadow: 0 2px 8px ${colors.shadow};
    `,
    evaluationTitle: css`
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 20px;
      color: ${colors.evaluationText};
      font-size: 18px;
      font-weight: 600;
      gap: 8px;
    `,
    evaluationSuccessIcon: css`
      font-size: 20px;
      color: ${colors.evaluationText};
    `,
    evaluationStarsRow: css`
      text-align: center;
      margin-bottom: 20px;
      padding: 16px;
      background: ${colors.evaluationFeedbackBg};
      border-radius: 12px;
      border: 1px solid ${colors.evaluationFeedbackBorder};
    `,
    evaluationRatingLabel: css`
      font-size: 14px;
      color: ${colors.textSecondary};
      margin-bottom: 12px;
      font-weight: 500;
    `,
    evaluationStarsContainer: css`
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      flex-wrap: wrap;
    `,
    evaluationEmoji: css`
      transition: all 0.2s ease;
    `,
    evaluationMetaBlue: css`
      margin-left: 12px;
      color: ${colors.evaluationMeta};
      font-weight: 500;
      font-size: 14px;
    `,
    evaluationFeedbackBox: css`
      background: ${colors.evaluationFeedbackBg};
      padding: 16px;
      border-radius: 12px;
      border: 1px solid ${colors.evaluationFeedbackBorder};
      margin-bottom: 16px;
      transition: background-color 0.3s ease, border-color 0.3s ease;
    `,
    evaluationFeedbackLabel: css`
      font-size: 14px;
      color: ${colors.textSecondary};
      margin-bottom: 10px;
      font-weight: 500;
    `,
    evaluationFeedbackText: css`
      color: ${colors.evaluationFeedbackText};
      line-height: 1.6;
      font-size: 14px;
      padding: 12px;
      background: ${colors.background};
      border-radius: 8px;
      border: 1px solid ${colors.border};
      word-break: break-word;
    `,
    evaluationThanks: css`
      text-align: center;
      color: ${colors.textSecondary};
      font-size: 13px;
      padding-top: 12px;
      border-top: 1px solid ${colors.border};
      margin-top: 8px;
      font-style: italic;
    `,
    evaluationSkippedBox: css`
      padding: 16px;
      text-align: center;
      color: ${colors.evaluationSkippedText};
      background: ${colors.evaluationSkippedBg};
      border-radius: 8px;
      border: 1px solid ${colors.evaluationSkippedBorder};
      transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;
    `,
    blueIconSmall: css`
      font-size: 16px;
      color: ${colors.evaluationMeta};
    `,
    /* ===== Extracted from component inline styles ===== */
    fullWidthRow: css`
      width: 100%;
    `,
    smallTimestamp: css`
      font-size: 12px;
    `,
    headerCloseWrapper: css`
      position: absolute;
      top: 10px;
      right: 22px;
      z-index: 1;
      display: flex;
      align-items: center;
      gap: 0;
    `,
    themeToggleButton: css`
      width: 35px;
      height: 35px;
      border-radius: 50%;
      background: ${colors.hoverBg};
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 8px ${colors.shadow};
      transition: all 0.3s ease;
      &:hover {
        background: ${colors.hoverBg};
        transform: scale(1.05);
      }
    `,
    themeIcon: css`
      color: ${colors.text};
      font-size: 16px;
      transition: color 0.3s ease;
    `,
    powerButton: css`
      width: 35px;
      height: 35px;
      border-radius: 50%;
      background: #ff4d4f;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 6px 18px rgba(255, 77, 79, 0.28);
    `,
    powerIcon: css`
      color: #fff;
      font-size: 16px;
    `,
    avatarImg: css`
      width: 24px;
      height: 24px;
      border-radius: 50%;
    `,
    hiddenFileInput: css`
      display: none;
    `,
    modalMediaFull: css`
      width: 100%;
    `,
    videoThumbWrapper: css`
      position: relative;
      width: 40px;
      height: 40px;
      cursor: pointer;
    `,
    videoThumbImage: css`
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 4px;
    `,
    videoPlayIcon: css`
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 16px;
      color: white;
      text-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
    `,
    layoutRelative: css`
      position: relative;
    `,
  };
}); 